import type { JSX } from 'react';

import type { KpowScore } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import { KPOW_BLUE, KPOW_RED, redBlueTrackGradient, signedColor } from '../kpowColors';
import { formatLean, signedToPercent } from '../kpowFormat';

interface IndexGaugeProps {
    /** Score to display; follows the chart cursor while scrubbing. Null before data loads. */
    composite: KpowScore | null;
}

/** Axis labels and their blue→purple→red color, matching the gradient bar beneath them. */
const AXIS_TICKS: { label: string; color: string }[] = [
    { label: '+50', color: '#265cff' },
    { label: '+30', color: '#265cff' },
    { label: '+20', color: '#4157e6' },
    { label: '+10', color: '#6a4fbf' },
    { label: '0', color: '#944798' },
    { label: '+10', color: '#c0406f' },
    { label: '+20', color: '#e73849' },
    { label: '+30', color: '#ff5a5a' },
    { label: '+50', color: '#ff5a5a' },
];

/**
 * Republican/Democrat lean band: a thin blue→red gradient bar with a marker at the current value,
 * the party letters at each end, and the "+X party lean" label floating above the marker. Lives at
 * the bottom of the chart card (Figma 753:705).
 */
const IndexGauge = ({ composite }: IndexGaugeProps): JSX.Element => {
    const value = composite?.signed_value ?? 0;
    const markerPct = signedToPercent(value);
    // Keep the marker and its floating label inside the track.
    const clampedPct = Math.max(6, Math.min(94, markerPct));
    const leanColor = signedColor(value);

    return (
        <div className="flex flex-col gap-1.5">
            {/* D  |  "+2.90 Republican lean" (over the marker)  |  R */}
            <div className="relative flex h-[30px] items-end justify-between">
                <span className="text-[24px] leading-none" style={{ color: KPOW_BLUE }}>
                    D
                </span>
                <span
                    className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-center text-[15px] leading-none tabular-nums"
                    style={{ left: `${clampedPct}%`, color: leanColor }}
                >
                    {composite ? formatLean(composite) : 'Even'}
                </span>
                <span className="text-[24px] leading-none" style={{ color: KPOW_RED }}>
                    R
                </span>
            </div>
            {/* Gradient bar with the current-value marker */}
            <div className="relative h-[6px] w-full rounded-full" style={{ background: redBlueTrackGradient() }}>
                <div
                    className="absolute top-1/2 h-[18px] w-[8px] -translate-x-1/2 -translate-y-1/2 rounded-[3px] border-[3px] border-solid border-core-white bg-core-black"
                    style={{ left: `${clampedPct}%` }}
                />
            </div>
            {/* Axis scale, colored to match the bar */}
            <div className="flex items-start justify-between">
                {AXIS_TICKS.map((tick, idx) => (
                    <span
                        key={`${tick.label}-${idx}`}
                        className="text-[12px] leading-4 tabular-nums"
                        style={{ color: tick.color }}
                    >
                        {tick.label}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default IndexGauge;
